import { useState, useContext, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Flex,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Heading,
  Spinner,
  useColorModeValue,
  FormErrorMessage,
  useToast,
  SimpleGrid,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import SelectSearch from 'components/fields/SelectSearch';

import {
  apiGetLeadTag,
  apiUpdateLeadTag,
  apiCreateLeadTag,
  apiGetBranches,
  apiGetUsers,
} from 'api';

import type {
  APILeadTagData,
  APIBranchData,
  APIUserListResultData,
} from 'types/api.types';

import { AuthContext } from 'contexts/AuthContext';

import { ROLE_ADMIN, ROLE_MANAGER } from 'variables/constants';

const TagFormView = () => {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const isAdmin = user.role === ROLE_ADMIN;
  const isManager = user.role === ROLE_MANAGER;
  const canEditBranch = isAdmin || isManager;
  const canEditUser = isAdmin || isManager;
  const isEditMode = !!id;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    user_id: '',
    branch_id: '',
  });

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch tag data if in edit mode
  const {
    data: tagData,
    isLoading: isLoadingTag,
    error: tagError,
  } = useQuery<APILeadTagData>({
    queryKey: ['lead-tag', id],
    queryFn: () => apiGetLeadTag(parseInt(id)),
    enabled: isEditMode,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch branches data (only for admin and manager)
  const { data: branchesData = [], isLoading: isLoadingBranches } = useQuery<
    APIBranchData[]
  >({
    queryKey: ['branches-all'],
    queryFn: () => apiGetBranches(null, 'all'),
    enabled: canEditBranch,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch users data (only for admins and managers)
  const { data: usersData = [], isLoading: isLoadingUsers } = useQuery<
    APIUserListResultData[]
  >({
    queryKey: ['all-users'],
    queryFn: () => apiGetUsers(null, 'all'),
    enabled: canEditUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Set form data when tag data is loaded
  useEffect(() => {
    if (tagData && isEditMode) {
      setFormData({
        name: tagData.name || '',
        user_id: tagData.user?.id?.toString() || '',
        branch_id: tagData.branch?.id?.toString() || '',
      });
    }
  }, [tagData, isEditMode]);

  // Create tag mutation
  const createTagMutation = useMutation({
    mutationFn: (data: any) => apiCreateLeadTag(data),
    onSuccess: () => {
      toast({
        title: 'Tag created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      navigate('/admin/settings/tags');
    },
    onError: handleApiError,
  });

  // Update tag mutation
  const updateTagMutation = useMutation({
    mutationFn: ({ tagId, data }: { tagId: number; data: any }) =>
      apiUpdateLeadTag(tagId, data),
    onSuccess: () => {
      toast({
        title: 'Tag updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      // Invalidate the tag query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['lead-tag', id] });
      navigate('/admin/settings/tags');
    },
    onError: handleApiError,
  });

  // Handle API errors
  function handleApiError(error: any) {
    toast({
      title: `Error ${isEditMode ? 'updating' : 'creating'} tag`,
      description: 'Please check the form for errors',
      status: 'error',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });

    const formattedErrors: Record<string, string> = {};

    // Check if error has response property (axios error)
    if (error.response && error.response.data) {
      Object.entries(error.response.data).forEach(
        ([field, messages]: [string, any]) => {
          if (Array.isArray(messages) && messages.length > 0) {
            formattedErrors[field] = messages[0];
          } else if (typeof messages === 'string') {
            formattedErrors[field] = messages;
          }
        },
      );
    }
    // Direct error object (not wrapped in response)
    else if (typeof error === 'object') {
      Object.entries(error).forEach(([field, messages]: [string, any]) => {
        if (Array.isArray(messages) && messages.length > 0) {
          formattedErrors[field] = messages[0];
        } else if (typeof messages === 'string') {
          formattedErrors[field] = messages;
        }
      });
    }

    setErrors(formattedErrors);
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear any previous errors for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleUserChange = (selectedOption: any) => {
    setFormData((prev) => ({
      ...prev,
      user_id: selectedOption,
      branch_id: selectedOption ? '' : prev.branch_id, // Clear branch if user is selected
    }));

    if (errors.user_id) {
      setErrors((prev) => ({
        ...prev,
        user_id: '',
      }));
    }
  };

  const handleBranchChange = (selectedOption: any) => {
    setFormData((prev) => ({
      ...prev,
      branch_id: selectedOption,
      user_id: selectedOption ? '' : prev.user_id, // Clear user if branch is selected
    }));

    if (errors.branch_id) {
      setErrors((prev) => ({
        ...prev,
        branch_id: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const tagPayload = {
        name: formData.name,
        branch_id: formData.branch_id ? parseInt(formData.branch_id) : null,
        user_id: formData.user_id ? parseInt(formData.user_id) : null,
      };

      // For regular users, always set branch_id to null and user_id to current user
      if (!canEditUser) {
        tagPayload.branch_id = null;
        tagPayload.user_id = user.id;
      }

      if (isEditMode && id) {
        // Update existing tag
        updateTagMutation.mutate({
          tagId: parseInt(id),
          data: tagPayload,
        });
      } else {
        // Create new tag
        createTagMutation.mutate(tagPayload);
      }

      queryClient.invalidateQueries({ queryKey: ['lead-tags'] });
    } catch (error) {
      toast({
        title: `Error ${isEditMode ? 'updating' : 'creating'} tag`,
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Show loading state
  if (
    (canEditBranch && isLoadingBranches) ||
    (canEditUser && isLoadingUsers) ||
    (isEditMode && isLoadingTag)
  ) {
    return (
      <Card>
        <Flex align="center" justify="center">
          <Spinner size="sm" mr="10px" /> Loading data...
        </Flex>
      </Card>
    );
  }

  // Show error state if tag data failed to load (in edit mode)
  if (isEditMode && tagError) {
    return (
      <Card>
        <Flex align="center" justify="center" color="red.500">
          Error loading tag data. Please try again.
        </Flex>
      </Card>
    );
  }

  const isSubmitting =
    createTagMutation.isPending || updateTagMutation.isPending;

  return (
    <form onSubmit={handleSubmit}>
      <Card mb="20px">
        <Heading size="md" mb={6} color={textColor}>
          {isEditMode ? 'Edit Tag' : 'Create New Tag'}
        </Heading>

        {errors.non_field_errors && (
          <Box mb={6} p={3} borderRadius="md" bg="red.100" color="red.700">
            {errors.non_field_errors}
          </Box>
        )}

        <SimpleGrid columns={{ base: 1, md: 1 }} spacing={6}>
          <FormControl isRequired isInvalid={!!errors.name}>
            <FormLabel>Name</FormLabel>
            <Input
              variant="main"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Tag Name"
            />
            <FormErrorMessage>{errors.name}</FormErrorMessage>
          </FormControl>

          {canEditBranch && (
            <FormControl isInvalid={!!errors.branch_id}>
              <FormLabel>Branch</FormLabel>
              <SelectSearch
                options={
                  branchesData?.map((branch) => ({
                    value: branch.id.toString(),
                    label: branch.name,
                  })) || []
                }
                value={formData.branch_id}
                onChange={handleBranchChange}
                placeholder="Select Branch"
                isDisabled={isLoadingBranches || !!formData.user_id}
                allowClear
              />
              <FormErrorMessage>{errors.branch_id}</FormErrorMessage>
            </FormControl>
          )}

          {canEditUser ? (
            <FormControl isInvalid={!!errors.user_id}>
              <FormLabel>User</FormLabel>
              <SelectSearch
                options={
                  usersData?.map((user) => ({
                    value: user.user.id.toString(),
                    label: `${user.user.first_name} ${user.user.last_name}`,
                  })) || []
                }
                value={formData.user_id}
                onChange={handleUserChange}
                placeholder="Select User"
                isDisabled={isLoadingUsers || !!formData.branch_id}
                allowClear
              />
              <FormErrorMessage>{errors.user_id}</FormErrorMessage>
            </FormControl>
          ) : (
            <FormControl>
              <FormLabel>User</FormLabel>
              <Input
                variant="main"
                value={`${user.first_name} ${user.last_name}`}
                disabled
              />
            </FormControl>
          )}
        </SimpleGrid>

        <Flex mt={10} justify="flex-end">
          <Button
            variant="outline"
            mr={3}
            onClick={() => navigate('/admin/settings/tags')}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            type="submit"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Updating' : 'Creating'}
          >
            {isEditMode ? 'Update Tag' : 'Create Tag'}
          </Button>
        </Flex>
      </Card>
    </form>
  );
};

export default TagFormView;
